import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Helper component for currency adornment
const CurrencyAdornment = () => (
  <Typography variant="body1" sx={{ mr: 1, color: 'text.secondary' }}>R</Typography>
);

const CashbookTransactionsTab = ({ matter, estateId, onSummaryUpdate, isOldDatabase }) => {
  const theme = useTheme();
  const { id } = useParams();
  const [transactions, setTransactions] = useState([]);
  const [selectedTransactionIndex, setSelectedTransactionIndex] = useState(null);
  const [transactionType, setTransactionType] = useState('');
  const [transactionDate, setTransactionDate] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [description, setDescription] = useState('');
  const [debitTransaction, setDebitTransaction] = useState(0);
  const [creditTransaction, setCreditTransaction] = useState(0);
  const [balance, setBalance] = useState('');
  const [customTransactionType, setCustomTransactionType] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isInvitedUser, setIsInvitedUser] = useState(false);
  const [user, setUser] = useState(null);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const [estate, setEstate] = useState(null);

  const legalMatterId = matter?.id || '';
  const estateIdFromMatter = matter?.EstateID || estateId || '';

  const [formData, setFormData] = useState({});
  

  const isValidUUID = (uuid) =>
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(uuid);

    useEffect(() => {
      const fetchUser = async () => {
        try {
          const res = await axios.get('https://ipms.insollex.com/api/user', { withCredentials: true });
    
          // Defensive check
          if (typeof res.data !== 'object' || res.data === null) {
            console.error("Invalid user data received:", res.data);
            setUser(null);
            return;
          }
    
          // Optional: additional checks
          if (!res.data.role) {
            console.warn("User has no role defined. Treating as guest.");
            res.data.role = "guest";
          }
    
          setUser(res.data);
        } catch (err) {
          console.error('Could not fetch user', err);
          setUser(null);
        }
      };
    
      fetchUser();
    }, []);

    const computeCashbookSummary = (transactions) => {
      const summary = {
        cashbookBalance: 0,
        cashbookTotalDEP: 0,
        cashbookTotalEXP: 0,
        cashbookTotalDVO: 0,
        cashbookTotalEVO: 0,
        cashbookTotalDBA: 0,
        cashbookTotalEBA: 0,
        cashbookTotalDEC: 0,
        cashbookTotalSDY: 0,
        cashbookTotalDEB: 0,
        cashbookTotalBAP: 0,
        cashbookTotalVSK: 0,
        cashbookTotalRPD: 0,
        cashbookTotalDRS: 0,
        cashbookTotalPMT: 0,
        cashbookTotalBDP: 0,
        cashbookTotalINV: 0,
        cashbookTotalBLN: 0,
        cashbookTotalREK: 0,
      };
    
      transactions.forEach((tx) => {
        const type = tx.transactionType;
        const debit = parseFloat(tx.debitTransaction || 0);
        const credit = parseFloat(tx.creditTransaction || 0);
    
        const typeCode = type?.split(' ')[0]?.toUpperCase(); // Get DEP, EXP, etc.
        const key = `cashbookTotal${typeCode}`;
    
        const netAmount = credit - debit;

        if (summary.hasOwnProperty(key)) {
          summary[key] += netAmount;
        }
    
        summary.cashbookBalance += netAmount;
      });
    
      // Round to 2 decimals
      for (const key in summary) {
        summary[key] = parseFloat(summary[key]).toFixed(2);
      }
    
      return summary;
    };

  const fetchTransactions = async () => {
    try {
      let request = null;
      if (legalMatterId) {
        request = axios.get(`https://ipms.insollex.com/api/transaction?legalMatterId=${legalMatterId}`);
      } else if (estateIdFromMatter && estateIdFromMatter !== '0') {
        // request = axios.get(`https://ipms.insollex.com/api/old-database/cashbook-transactions/${estateIdFromMatter}`);
      } else {
        setSnackbarMessage('Please provide either a valid Legal Matter ID or Estate ID.');
        setSnackbarSeverity('warning');
        setOpenSnackbar(true);
        setTransactions([]);
        return;
      }

      const response = await request;
      const transactionsArray = response.data?.data ?? response.data ?? [];

      if (Array.isArray(transactionsArray) && transactionsArray.length > 0) {
        setTransactions(transactionsArray);
      } else {
        setTransactions([]);
        setSnackbarSeverity('info');
        setOpenSnackbar(true);
      }

    } catch (error) {
      let message = 'Failed to fetch transactions due to a server or network error.';
      if (error?.response?.data?.message) {
        message = `Error: ${error.response.data.message}`;
      }
      setTransactions([]);
      setSnackbarMessage(message);
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  useEffect(() => {
    if (legalMatterId || estateIdFromMatter) {
      fetchTransactions();
    }
  }, [legalMatterId, estateIdFromMatter]);

  const resetForm = () => {
    setTransactionType('');
    setTransactionDate('');
    setPaymentReference('');
    setDescription('');
    setDebitTransaction(0);
    setCreditTransaction(0);
    setCustomTransactionType('');
    setBalance(0);
    setSelectedTransactionIndex(null);
    setSelectedTransactionId(null);
  };

  const handleEditTransactionById = (id) => {
    const index = transactions.findIndex(tx => tx.id === id);
    if (index === -1) return;
  
    const transaction = transactions[index];
  
    setSelectedTransactionIndex(index);
    setSelectedTransactionId(id);  // Store the id here!
  
    setTransactionType(transaction.transactionType);
    setTransactionDate(transaction.transactionDate);
    setPaymentReference(transaction.paymentReference);
    setDescription(transaction.description);
    setDebitTransaction(transaction.debitTransaction);
    setCreditTransaction(transaction.creditTransaction);
    setBalance(transaction.balance ?? '');
  };

  const handleDownloadPDF = () => {
  const doc = new jsPDF();
  const summary = computeCashbookSummary(transactions);

  // 1. Header & Branding
  doc.setFontSize(22);
  doc.setTextColor(40, 40, 40);
  doc.text("CASHBOOK REPORT", 14, 20);
  
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text("Integrated Property Management System (IPMS)", 14, 27);
  doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 32);

  // 2. Estate Information Box
  doc.setDrawColor(200);
  doc.line(14, 38, 196, 38); // Horizontal line

  doc.setFontSize(12);
  doc.setTextColor(0);
  doc.setFont(undefined, 'bold');
  doc.text("Matter Details", 14, 48);
  
  doc.setFont(undefined, 'normal');
  doc.setFontSize(10);
  doc.text(`Matter Name: ${matter?.matterName || 'N/A'}`, 14, 55);
  doc.text(`Estate Number: ${matter?.estateNo || 'N/A'}`, 14, 61);
  doc.text(`Client: ${matter?.client || 'N/A'}`, 14, 67);

  // 3. Transactions Table
  const tableRows = transactionsWithBalances.map(tx => [
    tx.transactionDate,
    tx.transactionType,
    tx.paymentReference,
    tx.description,
    parseFloat(tx.debitTransaction || 0).toFixed(2),
    parseFloat(tx.creditTransaction || 0).toFixed(2),
    tx.runningBalance
  ]);

  autoTable(doc, {
    startY: 75,
    head: [["Date", "Type", "Ref", "Description", "Debit (R)", "Credit (R)", "Balance (R)"]],
    body: tableRows,
    theme: 'grid',
    headStyles: { fillColor: [44, 62, 80], fontSize: 9, halign: 'center' }, // Dark Blue/Grey header
    columnStyles: {
      4: { halign: 'right' },
      5: { halign: 'right' },
      6: { halign: 'right', fontStyle: 'bold' }
    },
    styles: { fontSize: 8, cellPadding: 2 },
    alternateRowStyles: { fillColor: [245, 245, 245] }
  });

  // 4. Summary Totals (Positions relative to the end of the table)
  const finalY = doc.lastAutoTable.finalY + 15;
  
  // Draw a summary box
  doc.setFillColor(249, 249, 249);
  doc.rect(130, finalY - 5, 66, 30, 'F'); 
  doc.setDrawColor(220);
  doc.rect(130, finalY - 5, 66, 30, 'D');

  doc.setFontSize(10);
  doc.setTextColor(0);
  doc.text(`Total Deposits:`, 135, finalY + 5);
  doc.text(`R ${summary.cashbookTotalDEP}`, 190, finalY + 5, { align: 'right' });
  
  doc.text(`Total Expenses:`, 135, finalY + 12);
  doc.text(`R ${summary.cashbookTotalEXP}`, 190, finalY + 12, { align: 'right' });

  doc.setFont(undefined, 'bold');
  doc.text(`Closing Balance:`, 135, finalY + 20);
  doc.text(`R ${summary.cashbookBalance}`, 190, finalY + 20, { align: 'right' });

  // 5. Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`Page ${i} of ${pageCount} - Confidential Financial Document`, 105, 285, { align: 'center' });
  }

  doc.save(`IPMS_Report_${matter?.estateNo || 'Export'}.pdf`);
};

  const handleImportExcel = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws);
      
      const cleanedData = data.map(item => ({
        transactionDate: item.Date || new Date().toISOString().split('T')[0],
        transactionType: item.Type || 'DEP - General Receipts',
        paymentReference: item.Reference || '',
        description: item.Description || '',
        debitTransaction: parseFloat(item.Debit || 0),
        creditTransaction: parseFloat(item.Credit || 0),
      }));

      console.log("Cleaned Import:", cleanedData);
      setSnackbarMessage(`Successfully loaded ${cleanedData.length} records. Click Save to confirm.`);
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    };
    reader.readAsBinaryString(file);
  };

  const handleDeleteTransaction = async (transactionId) => {
    try {
      await axios.delete(`https://ipms.insollex.com/api/transaction/${transactionId}`);
      fetchTransactions();
      setSnackbarMessage('Transaction deleted successfully.');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (error) {
      setSnackbarMessage('Error deleting transaction.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleAddOrSaveTransaction = async () => {
  try {
    const isOldDb = isOldDatabase ?? false;

    // 🔒 Shared Validation
    if (!isOldDb && (!legalMatterId || !isValidUUID(legalMatterId))) {
      throw new Error('Legal matter ID is missing or invalid.');
    }

    if (parseFloat(debitTransaction) === 0 && parseFloat(creditTransaction) === 0) {
      throw new Error('Either Debit or Credit must be greater than 0.');
    }

    const formattedDate = new Date(transactionDate).toISOString().slice(0, 19).replace('T', ' ');

    if (isOldDb) {
      // 🏁 OLD DB TRANSACTION
      const oldDbTransaction = {
        EstateID: estateIdFromMatter,
        SequenceNo: selectedTransactionId || 0, // For update or placeholder for insert
        TransactionType: transactionType === 'Other' ? customTransactionType : transactionType,
        Date: formattedDate,
        Reference: paymentReference,
        Description1: description.slice(0, 100),
        Description2: '', // Optional
        Debit: parseFloat(debitTransaction) || 0,
        Credit: parseFloat(creditTransaction) || 0,
        Balance: parseFloat(balance) || 0,
      };

      if (selectedTransactionId) {
        // 🔄 Update old transaction
        // await axios.put(
        //   `https://ipms.insollex.com/api/old-database/cashbook-transactions/${selectedTransactionId}`,
        //   oldDbTransaction
        // );
        //
        // setSnackbarMessage('Old DB transaction updated successfully.');
      } else {
        // ➕ Insert into old DB (requires working backend route)
        // const response = await axios.post(
        //   'https://ipms.insollex.com/api/old-database/cashbook-transactions',
        //   oldDbTransaction
        // );
        //
        // setTransactions([...transactions, response.data]);
        // setSnackbarMessage('Old DB transaction added successfully.');
      }
    } else {
      // 🆕 NEW DB TRANSACTION
      const newTransaction = {
        transactionDate,
        transactionType: transactionType === 'Other' ? customTransactionType : transactionType,
        paymentReference,
        description,
        debitTransaction: parseFloat(debitTransaction) || 0,
        creditTransaction: parseFloat(creditTransaction) || 0,
        balance: parseFloat(balance) || 0,
        legalMatterId,
        estateId: estateIdFromMatter,
      };

      if (selectedTransactionId) {
        await axios.put(
          `https://ipms.insollex.com/api/transaction/${selectedTransactionId}`,
          newTransaction
        );

        setSnackbarMessage('Transaction updated successfully.');
      } else {
        const response = await axios.post(
          'https://ipms.insollex.com/api/transaction',
          newTransaction
        );

        setTransactions([...transactions, response.data]);
        setSnackbarMessage('Transaction added successfully.');
      }
    }

    // ✅ Cleanup
    resetForm();
    setSelectedTransactionId(null);
    setSelectedTransactionIndex(null);
    fetchTransactions();

    setSnackbarSeverity('success');
    setOpenSnackbar(true);
  } catch (error) {
    setSnackbarMessage(error.message || 'Error saving transaction.');
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
  }
};
  

  const handleCloseSnackbar = (_, reason) => {
    if (reason === 'clickaway') return;
    setOpenSnackbar(false);
  };

  const isCredit = (type) => {
    const creditTypes = [
      'Credits',
      'DEP - General Receipts',
      'DVO - Deposit Free Surplus',
      'DBA - Deposit Assets Account',
      'DEC - Deposit Debtors Ceded',
      'DEB - Debtors Receipts',
      'BAP - Court Bond',
      'VSK - Advances from Administrator',
      'BDP - Investment Repaid',
      'INV - Investments',
      'INT - Interest Received',
      'DIV - Dividends Paid',
    ];
    return creditTypes.includes(type);
  };
  
  // Calculate running balances per account type
  const processed = {};
  const sortedTxs = [...transactions].sort((a, b) => new Date(a.transactionDate) - new Date(b.transactionDate));
  
  const transactionsWithBalances = sortedTxs.map((tx) => {
    const type = tx.transactionType;
    const debit = parseFloat(tx.debitTransaction || 0);
    const credit = parseFloat(tx.creditTransaction || 0);
  
    if (!processed[type]) processed[type] = 0;
  
    // Update balance
    if (isCredit(type)) {
      processed[type] += credit;
      processed[type] -= debit;
    } else {
      processed[type] += credit;
      processed[type] -= debit;
    }
  
    return {
      ...tx,
      runningBalance: processed[type].toFixed(2),
    };
  });

  const checkIfNewMatter = (id) => {
    if (!id) return false;
  
    // Improved regular expression to match UUID format (including lowercase and uppercase letters, digits, and hyphens)
    const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  
    // Return true if the ID matches the UUID format
    return uuidRegex.test(id);
  };
  
  const formatDate = (date) => {
    return date ? new Date(date).toISOString().split('T')[0] : '';
};

  useEffect(() => {
    if (transactions.length > 0 && onSummaryUpdate) {
      const summary = computeCashbookSummary(transactions);
      onSummaryUpdate(summary); // Send to parent
    }
  }, [transactions]);

  useEffect(() => {
    const run = async () => {
      const estateIdFinal = matter?.EstateID || estateId || '';
      const isNewMatter = checkIfNewMatter(id);
    
      // Use local transactions if present
      if (Array.isArray(matter?.transaction) && matter.transaction.length > 0) {
        const formattedTransactions = matter.transaction.map((txn) => ({
          ...txn,
          transactionDate: formatDate(txn.transactionDate),
          createdAt: formatDate(txn.createdAt),
          updatedAt: formatDate(txn.updatedAt),
          // Add other date fields here as needed
        }));
    
        const totals = calculateCashbookTotals(formattedTransactions);
        setTransactions(formattedTransactions);
        setFormData((prev) => ({ ...prev, ...totals }));
        return;
      }
    
      // Validate LegalMatterId or EstateId presence
      if (!legalMatterId && (!estateIdFinal || estateIdFinal === '0')) {
        setSnackbarMessage('Please provide either a valid Legal Matter ID or Estate ID.');
        setSnackbarSeverity('warning');
        setOpenSnackbar(true);
        setTransactions([]);
        return;
      }
    
      try {
        let request;
    
        if (legalMatterId) {
          request = axios.get(`https://ipms.insollex.com/api/transaction?legalMatterId=${legalMatterId}`);
        } else {
          // request = axios.get(`https://ipms.insollex.com/api/old-database/cashbook-transactions/${estateIdFinal}`);
        }
    
        if (request) {
        const response = await request;
        const transactionsArray = response.data?.data ?? response.data ?? [];
    
        if (Array.isArray(transactionsArray) && transactionsArray.length > 0) {
          const formattedTransactions = transactionsArray.map((txn) => ({
            ...txn,
            transactionDate: formatDate(txn.transactionDate),
            createdAt: formatDate(txn.createdAt),
            updatedAt: formatDate(txn.updatedAt),
            // Add other date fields here as needed
          }));
    
          const totals = calculateCashbookTotals(formattedTransactions);
          setTransactions(formattedTransactions);
          setFormData((prev) => ({ ...prev, ...totals }));
        } else {
          setTransactions([]);
          setSnackbarSeverity('info');
          setSnackbarMessage('No transactions found.');
          setOpenSnackbar(true);
        }
        }
      } catch (error) {
        console.error(error);
        const message = error?.response?.data?.message
          ? `Error: ${error.response.data.message}`
          : 'Failed to fetch transactions due to a server or network error.';
        setSnackbarMessage(message);
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        setTransactions([]);
      }
    };
    
    if (matter) run();
  }, [matter, legalMatterId, estateId]);

  // Helper function to calculate totals from transactions
  const calculateCashbookTotals = (transactionsArray = []) => {
    const totals = {
      cashbookTotalDEP: 0,
      cashbookTotalEXP: 0,
      cashbookTotalDVO: 0,
      cashbookTotalEVO: 0,
      cashbookTotalDBA: 0,
      cashbookTotalEBA: 0,
      cashbookTotalDEC: 0,
      cashbookTotalSDY: 0,
      cashbookTotalDEB: 0,
      cashbookTotalBAP: 0,
      cashbookTotalVSK: 0,
      cashbookTotalRPD: 0,
      cashbookTotalDRS: 0,
      cashbookTotalPMT: 0,
      cashbookTotalBDP: 0,
      cashbookTotalINV: 0,
      cashbookTotalBLN: 0,
      cashbookBalance: 0,
      cashbookTotalREK: 0,
    };
  
    transactionsArray.forEach((tx) => {
      const typeCode = tx.transactionType?.split(' ')[0]?.toUpperCase();
  
      const credit = Number(tx.creditTransaction) || 0;
      const debit = Number(tx.debitTransaction) || 0;
      const netAmount = credit - debit;
  
      if (typeCode && totals.hasOwnProperty(`cashbookTotal${typeCode}`)) {
        totals[`cashbookTotal${typeCode}`] += netAmount;
      }
  
      // Accumulate balance (sum of all credit - debit)
      totals.cashbookBalance += netAmount;
    });
  
    // Format all totals to 2 decimals
    Object.keys(totals).forEach(key => {
      totals[key] = totals[key].toFixed(2);
    });
  
    return totals;
  };
  

return (
  <Grid container spacing={2}> {/* [1] Top Level Container */}
    <Grid item xs={12}>
      <Typography variant="h6" align="center" sx={{ color: 'text.primary', mb: 1 }}>
        Cashbook Transactions
      </Typography>
    </Grid>

    <Grid container item xs={12} spacing={2}> {/* [2] Main Content Grid */}
      {/* Transaction Entry Form */}
      <Grid item xs={12}>
        <Paper sx={{
          p: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          bgcolor: 'background.paper',
          boxShadow: theme.shadows[2],
          mb: 2,
        }}>
          <Grid container spacing={2}>
            {[
              { label: 'Transaction Date', type: 'date', value: transactionDate, setValue: setTransactionDate },
              {
                label: 'Transaction Type',
                type: 'select',
                value: transactionType,
                setValue: setTransactionType,
                options: [
                  'DEP - General Receipts', 'EXP - General Expenses', 'DVO - Deposit Free Surplus',
                  'EVO - Expenses Free Surplus', 'DBA - Deposit Assets Account', 'EBA - Expenses Assets Account',
                  'SDY - Bank Charges, Other', 'DEB - Debtors Receipts', 'BAP - Court Bond',
                  'BDP - Investment Repaid', 'INV - Investments', 'INT - Interest Received',
                  'DIV - Dividends Paid', 'REK - Bank Balance Statement'
                ],
              },
              { label: 'Payment Reference', value: paymentReference, setValue: setPaymentReference },
              { label: 'Description', value: description, setValue: setDescription },
              { label: 'Debit Transaction', type: 'number', value: debitTransaction, setValue: setDebitTransaction },
              { label: 'Credit Transaction', type: 'number', value: creditTransaction, setValue: setCreditTransaction },
            ].map((field, idx) => (
              <Grid item xs={12} sm={6} key={idx}>
                {field.type === 'select' ? (
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                      value={field.value}
                      onChange={(e) => field.setValue(e.target.value)}
                      label={field.label}
                      disabled={user?.role === 'guest' || user?.isInvited}
                    >
                      {field.options.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    label={field.label}
                    type={field.type || 'text'}
                    value={field.value}
                    onChange={(e) => field.setValue(e.target.value)}
                    InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
                    disabled={user?.role === 'guest' || user?.isInvited}
                    InputProps={
                      (field.label.includes('Transaction') && field.type === 'number')
                        ? { startAdornment: <CurrencyAdornment /> }
                        : undefined
                    }
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Grid>

      {/* Form Actions */}
      <Grid item xs={12}>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            onClick={handleAddOrSaveTransaction}
            disabled={user?.role === 'guest' || user?.isInvited}
          >
            {selectedTransactionId === null ? 'Save' : 'Update'}
          </Button>
          <Button variant="outlined" onClick={resetForm} disabled={user?.role === 'guest' || user?.isInvited}>
            Cancel
          </Button>
        </Box>
      </Grid>

      {/* Summary Dashboard */}
      <Grid item xs={12}>
  <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: 2, mb: 2 }}>
    <Typography variant="h6" sx={{ marginBottom: 2 }}>
      CashBook Transactions – Summary
    </Typography>
    <Grid container spacing={2}>
      {/* Restored Main Balance Field */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="CashBook Balance"
          name="cashbookBalance"
          type="number"
          value={computeCashbookSummary(transactions).cashbookBalance}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: <CurrencyAdornment />,
            style: {
              color: Number(computeCashbookSummary(transactions).cashbookBalance) < 0 ? 'red' : undefined,
              fontWeight: Number(computeCashbookSummary(transactions).cashbookBalance) < 0 ? 'bold' : 'normal',
            }
          }}
          disabled
        />
      </Grid>

      {/* Restored Totals List */}
      {[
        'DEP', 'EXP', 'DVO', 'EVO', 'DBA', 'EBA', 'SDY', 'DEB',
        'BAP', 'DRS', 'BDP', 'INV', 'BLN', 'REK'
      ].map(type => {
        const key = `cashbookTotal${type}`;
        const val = computeCashbookSummary(transactions)[key];
        return (
          <Grid item xs={12} sm={6} key={key}>
            <TextField
              fullWidth
              label={`Cashbook Total ${type}`}
              name={key}
              type="number"
              value={val}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: <CurrencyAdornment />,
                style: {
                  color: Number(val) < 0 ? 'red' : undefined,
                  fontWeight: Number(val) < 0 ? 'bold' : 'normal',
                }
              }}
              disabled
            />
          </Grid>
        );
      })}
    </Grid>
  </Box>
</Grid>

<Grid item xs={12}>
  <Box display="flex" justifyContent="flex-end" gap={2} mb={1}>
    {/* Import Logic */}
    <input
      type="file"
      accept=".xlsx, .xls"
      id="excel-import-input"
      style={{ display: 'none' }}
      onChange={handleImportExcel}
    />
    <label htmlFor="excel-import-input">
      <Button 
        variant="outlined" 
        component="span" 
        disabled={user?.role === 'guest' || user?.isInvited}
      >
        Import Excel
      </Button>
    </label>

    {/* PDF Download */}
    <Button 
      variant="contained" 
      color="error" 
      onClick={handleDownloadPDF}
      disabled={transactions.length === 0}
    >
      Download PDF Report
    </Button>
  </Box>
</Grid>

      {/* Transactions Table */}
      <Grid item xs={12}>
        <Paper sx={{ p: 0, border: `1px solid ${theme.palette.divider}`, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.action.hover }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Ref</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Debit (-)</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Credit (+)</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Balance</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>No transactions found.</TableCell>
                </TableRow>
              ) : (
                transactionsWithBalances.map((tx) => (
                  <TableRow key={tx.id} hover>
                    <TableCell>{tx.transactionDate}</TableCell>
                    <TableCell><Typography variant="caption">{tx.transactionType}</Typography></TableCell>
                    <TableCell>{tx.paymentReference}</TableCell>
                    <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {tx.description}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>
                      {Number(tx.debitTransaction) > 0 ? `R ${Number(tx.debitTransaction).toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>
                      {Number(tx.creditTransaction) > 0 ? `R ${Number(tx.creditTransaction).toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>R {tx.runningBalance}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleEditTransactionById(tx.id)} disabled={user?.role === 'guest' || user?.isInvited}>
                        Edit
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteTransaction(tx.id)} disabled={user?.role === 'guest' || user?.isInvited}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      </Grid>
    </Grid> {/* Closes [2] Main Content Grid */}

    <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar}>
      <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
        {snackbarMessage}
      </Alert>
    </Snackbar>
 </Grid>
  ); 
}; 


export default CashbookTransactionsTab;